import type React from 'react';

// The viewer is remounted for every asset. What the previous asset showed (an
// image or a video frame) is kept on screen while the next one of the same
// kind loads, so fast loads swap without an empty frame. Only the asset just
// left counts: any viewer that mounts clears it, whatever it shows, so e.g.
// video -> image -> video does not bring back the first video's frame.
export type LastShownMedia =
  | { kind: 'image'; src: string; style: React.CSSProperties; hiddenAt: number }
  | { kind: 'video'; canvas: HTMLCanvasElement; hiddenAt: number };

export type LastShownImage = Extract<LastShownMedia, { kind: 'image' }>;

// Only when navigating, not when the viewer is opened again later
const LAST_SHOWN_MEDIA_MAX_AGE_MS = 1000;

let lastShownMedia: LastShownMedia | null = null;

export function setLastShownMedia(media: LastShownMedia | null) {
  lastShownMedia = media;
}

export function clearLastShownMedia() {
  lastShownMedia = null;
}

/** The previous asset's media when it is of `kind` and was just hidden */
export function takeLastShownMedia<K extends LastShownMedia['kind']>(
  kind: K
): Extract<LastShownMedia, { kind: K }> | null {
  const media = lastShownMedia;
  lastShownMedia = null;
  if (
    !media ||
    media.kind !== kind ||
    Date.now() - media.hiddenAt > LAST_SHOWN_MEDIA_MAX_AGE_MS
  ) {
    return null;
  }
  return media as Extract<LastShownMedia, { kind: K }>;
}
